<template>
  <div
    v-if="hasRequestContentResponse && requestContentItem"
    class="flex flex-wrap items-center gap-2 dark:text-zinc-300 text-xs sm:text-sm"
    data-testid="request-content-info-component"
  >
    <div class="flex items-center gap-1.5">
      <Icon size="xs" name="general.chatAnswer" />
      <span class="font-semibold">Reply to:</span>
    </div>
    <div class="flex truncate items-center gap-2">
      <p data-testid="request-content-info-name" class="truncate">{{ requestContentItem.name }}</p>
      <a
        data-testid="request-content-info-link"
        class="text-blue-500"
        v-if="requestContentItem.requestLink && !hidesRedundantLinks"
        :href="requestContentItem.requestLink"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Request Link"
      >
        <Icon size="xs" name="general.externalLink" />
        <span class="sr-only">External link to the request</span>
      </a>
    </div>
    <template v-if="requestContentItem.customId">
      <span class="flex-shrink-0 text-slate-300 dark:text-zinc-700 font-light">|</span>
      <p data-testid="request-content-info-custom-id" class="flex-shrink-0">Custom ID: {{ requestContentItem.customId }}</p>
    </template>
    <!-- The Logs panel's Request Details card already surfaces the request
         link and collection, and its own header already has a "Download
         logs" button, so this row skips all three for the owner when that
         card is showing (desktop Logs mode) to avoid duplicating the same
         links twice on screen. On mobile Logs mode shows a "switch to
         desktop" notice instead of the card, so these stay. -->
    <template v-if="!hidesRedundantLinks">
      <span class="flex-shrink-0 text-slate-300 dark:text-zinc-700 font-light">|</span>
      <a class="text-blue-500 flex gap-1 items-center" :href="`/collections/${requestContentItem.collectionId}`" target="_blank" rel="noopener noreferrer">
        <Icon size="xs" name="general.turnDownRight" />
        Collection
      </a>
      <template v-if="insightsURL">
        <span class="flex-shrink-0 text-slate-300 dark:text-zinc-700 font-light">|</span>
        <a
          class="text-blue-500 flex gap-1 items-center"
          data-testid="request-content-info-download-logs"
          :href="insightsURL"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon size="xs" name="general.downloadFile" />
          Download logs
        </a>
      </template>
    </template>
    <span v-if="requestParamsToDisplay.length" class="flex-shrink-0 text-slate-300 dark:text-zinc-700 font-light">|</span>
    <template v-for="param in requestParamsToDisplay" :key="param.key">
      <p class="property" :data-testid="`request-content-info-query-params-${param.key}`">{{ param.key }}: {{ param.value }}</p>
      <span class="divider">|</span>
    </template>

    <template v-if="requestContentParamsCount > REQUEST_PARAMS_TO_DISPLAY_COUNT">
      <button
        type="button"
        class="text-blue-500 flex gap-1 items-center"
        @click.stop="showRequestContentParamsModal"
        data-testid="request-content-info-query-params-more"
      >
        More
      </button>
    </template>
  </div>
</template>
<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useMediaQuery } from '@vueuse/core';
import { Icon } from '@/storybook';
import { useRequestContentInfoPage, REQUEST_PARAMS_TO_DISPLAY_COUNT } from '../composables/useRequestContentInfoPage';
import { useViewerItemStore } from '../store/useViewerItemStore';
import { useArtifactsStore } from '../store/useArtifactsStore';
import { useViewerModeStore } from '../store/useViewerModeStore';
import { useUser } from '../composables/useUser';

const { loadData, showRequestContentParamsModal, requestContentItem, requestParamsToDisplay, requestContentParamsCount } = useRequestContentInfoPage();

const { hasRequestContentResponse } = useViewerItemStore();
const { insightsURL } = useArtifactsStore();
const { isLogsMode } = storeToRefs(useViewerModeStore());
const { isCurrentItemOwner } = useUser();
// Same breakpoint as useViewerAvailableModes — reads it directly here rather
// than re-invoking that composable, which would register a second redundant
// mode-fallback watcher.
const isMobile = useMediaQuery('(max-width: 1023px)');

// The Request Details card only renders in desktop Logs mode for the item
// owner (VideoOwnerLayout only — the guest layout never mounts it), so only
// hide the redundant links here when that card is actually the thing
// replacing them. Non-owner viewers (guests, same-org team members) keep
// seeing these links even in desktop Logs mode, since nothing else would
// surface the collection/request link for them otherwise.
const hidesRedundantLinks = computed(() => isLogsMode.value && !isMobile.value && isCurrentItemOwner.value);

onMounted(() => {
  if (hasRequestContentResponse) {
    loadData();
  }
});
</script>

<style scoped>
.property {
  @apply flex-shrink-0 max-w-[30ch] truncate;
}
.divider {
  @apply flex-shrink-0 text-slate-300 dark:text-zinc-700 font-light;
}
</style>
